import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { schedulesService } from "../services/schedules.service";
import type { HolidayInput, ScheduleAssignInput, ScheduleInput, ShiftInstance } from "../types";

/**
 * ⚠️ Sahifalanmagan ro'yxat — `GET /schedules` `page`/`limit` qabul qilmaydi
 * (servisdagi izohga qarang). `isActive` esa MAJBURIY parametr: backend
 * standarti `true`, ya'ni faolsizlantirilgan jadval filtrsiz ro'yxatda umuman
 * ko'rinmaydi va uni qayta yoqishning yo'li qolmasdi.
 */
export function useSchedulesList(isActive: boolean) {
	return useQuery({
		queryKey: ["schedules", { isActive }],
		queryFn: () => schedulesService.list(isActive),
	});
}

interface EmployeeOption {
	id: string;
	fullName: string;
}

interface DepartmentOption {
	id: string;
	name: string;
}

/** Xodim tanlash — server tomonida qidiriladi (`useEmployeeOptions` naqshi). */
export function useEmployeeOptions(search: string) {
	return useQuery({
		queryKey: ["employees", "schedule-options", search],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };

			if (search.trim()) {
				params.q = search.trim();
			}

			const response = await apiClient.get<{
				success: true;
				data: { items: EmployeeOption[] };
			}>("/employees", { params });

			return response.data.data.items;
		},
		staleTime: 30_000,
	});
}

/** Bo'limlar sahifalanmagan tekis ro'yxat qaytaradi (1b-1). */
export function useDepartmentOptions() {
	return useQuery({
		queryKey: ["departments", "all"],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: DepartmentOption[] }>(
				"/departments"
			);
			return response.data.data;
		},
		staleTime: 60_000,
	});
}

function useSchedulesMutation<TVariables>(
	mutationFn: (variables: TVariables) => Promise<unknown>,
	queryKey: unknown[],
	successMessageKey: string,
	errorFallbackKey: string
) {
	const { t } = useTranslation("schedules");
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey });
			message.success(t(successMessageKey));
		},
		onError: (error) => {
			// `messages.schedules` aniq sabab beradi: haftalik kunlar yo'q,
			// sikl to'liq emas, ish kunlari sikldan katta, davr kesishmoqda.
			message.error(getErrorMessage(error, t(errorFallbackKey)));
		},
	});
}

export function useCreateSchedule() {
	return useSchedulesMutation(
		(input: ScheduleInput) => schedulesService.create(input),
		["schedules"],
		"messages.scheduleCreated",
		"messages.scheduleCreateError"
	);
}

export function useUpdateSchedule() {
	return useSchedulesMutation(
		({ id, input }: { id: string; input: Partial<ScheduleInput> }) =>
			schedulesService.update(id, input),
		["schedules"],
		"messages.scheduleUpdated",
		"messages.scheduleUpdateError"
	);
}

export function useSetScheduleActive() {
	return useSchedulesMutation(
		({ id, isActive }: { id: string; isActive: boolean }) =>
			schedulesService.setActive(id, isActive),
		["schedules"],
		"messages.scheduleActiveUpdated",
		"messages.scheduleActiveUpdateError"
	);
}

export function useScheduleAssignments(scheduleId: string | null) {
	return useQuery({
		queryKey: ["schedules", scheduleId, "assignments"],
		queryFn: () => schedulesService.listAssignments(scheduleId as string),
		enabled: scheduleId !== null,
	});
}

export function useAssignSchedule(scheduleId: string | null) {
	return useSchedulesMutation(
		(input: ScheduleAssignInput) => schedulesService.assign(input),
		["schedules", scheduleId, "assignments"],
		"messages.assignmentCreated",
		"messages.assignmentCreateError"
	);
}

export function useRemoveScheduleAssignment(scheduleId: string | null) {
	return useSchedulesMutation(
		(id: string) => schedulesService.removeAssignment(id),
		["schedules", scheduleId, "assignments"],
		"messages.assignmentRemoved",
		"messages.assignmentRemoveError"
	);
}

export function useHolidaysList(year: number) {
	return useQuery({
		queryKey: ["holidays", year],
		queryFn: () => schedulesService.listHolidays(year),
	});
}

/**
 * ⚠️ Invalidatsiya kaliti — YILSIZ `["holidays"]` prefiksi. Yil bilan
 * cheklansa, keshda turgan BOSHQA yillar eskirgan holda qolardi: forma
 * yilni chegaralaydi, lekin bayram o'chirilishi generatorga ta'sir qiladi
 * va foydalanuvchi yillar orasida erkin almashadi.
 */
export function useCreateHoliday() {
	return useSchedulesMutation(
		(input: HolidayInput) => schedulesService.createHoliday(input),
		["holidays"],
		"messages.holidayCreated",
		"messages.holidayCreateError"
	);
}

export function useDeleteHoliday() {
	return useSchedulesMutation(
		(id: string) => schedulesService.removeHoliday(id),
		["holidays"],
		"messages.holidayDeleted",
		"messages.holidayDeleteError"
	);
}

export interface ShiftInstanceFilters {
	from: string;
	to: string;
	employeeId?: string;
}

/**
 * ⚠️ So'rov parametrlari `from` / `to` — `fromDate` / `toDate` EMAS
 * (`ShiftInstanceListQuerySchema`). Nomlar mos kelmasa filtr jimgina
 * tushib qolardi: sxema `strictObject` emas, ya'ni noma'lum kalit rad
 * etilmaydi va butun ufq qaytarilardi.
 */
export function useShiftInstancesList(filters: ShiftInstanceFilters) {
	const params: Record<string, string> = {
		from: filters.from,
		to: filters.to,
	};

	if (filters.employeeId) {
		params.employeeId = filters.employeeId;
	}

	return usePaginatedQuery<ShiftInstance>({
		queryKey: ["shift-instances"],
		url: "/shift-instances",
		params,
	});
}
